/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The mapping control's teaching text, in one place and behind the hover** (#1179, `X28b`,
 * `XS3` slice 3).
 *
 * Todd, 2026-08-27: *"the help text is starting to get pretty long in many places, and most of it
 * is targeted at 1st time users… move long description text to the `i` fly-over and only use short
 * text (when necessary) on the primary view."* And again on 2026-09-03, opening this slice: *"It's
 * hard to evaluate the layout with all the text on the screen."*
 *
 * The line this file draws is `X28b`'s, and getting it wrong undoes pattern 24:
 *
 * | Text | Where it lives |
 * | --- | --- |
 * | What this control is, and what it does | **here**, behind the hover |
 * | Why this control is **disabled here** | on the surface, always visible — a `CapabilityRefused.reason` |
 * | A warning about **this row's** value | on the surface |
 *
 * So nothing in this file is about a particular row: every string here is true of the control on
 * every surface, every time. A sentence that is only true of *this* row belongs on the row, and a
 * sentence that says *why you cannot do this here* belongs beside the disabled control — *"a
 * disabled control whose reason is behind a hover is an absent control again"*.
 *
 * **It is not a dumping ground** (#1179's own rule): a stale sentence inside a popover is exactly as
 * much a defect as one on the row, and hiding it makes it *less* likely to be corrected. Two strings
 * were **deleted** rather than moved when this landed — see `MappingControl`'s `UNRESOLVED_HINT`
 * note — because the control that now answers the question says it better and per-answer.
 */

/** The four zones of the value line (`X23`, canvas `1a`), plus the controls inside them. */
export const MAPPING_HELP = {
  /**
   * Zone 1 · **where to put it**. The claim never changes a byte the driver sends, which is the
   * whole of `X46` and the thing an operator most needs told — it was one boolean with the wire
   * answer until 2026-09-02.
   */
  importance:
    'Says the receiver expects a value here. It changes nothing about the request itself — it decides what the row is marked with, and what the Prove phase asks you to fill in.',

  /**
   * Zone 1 · **what the value is** (`CR2`, `#1371`). States the three outcomes an operator cannot see
   * from the box: where it is stored, that it will not be shown again, and that only a fixed value
   * has anything to store.
   */
  secret:
    'For a credential such as an API key or a token. A fixed value is stored encrypted, sent to this partner as typed, and never shown again: you can replace or clear it. A value that comes from a session path or a macro is not stored, so there is nothing to encrypt.',

  /**
   * Zone 2 · **where to get it**. `X24`: the kind is derived and reported, never chosen, so the
   * sentence has to say that the operator does not have to pick a mode — that is the single most
   * surprising property of the field, and the tab strip is what used to say it.
   */
  value:
    'A fixed value, a session path such as session.content.genre, or a {macro}. The chip at the head of the field reads back what you typed as — there is nothing to choose.',

  /**
   * Zone 3 · **the default**, and it **defers to the omission answer** rather than restating it.
   *
   * Reported by Todd 2026-09-03: *"the help text is confusing vs the options in the 'on the wire'
   * drop down."* It was. `UNRESOLVED_HINT` said *"leave Default blank to send nothing when it does
   * not resolve"* while the select's own sentence said *"it goes out when it resolves, and is left
   * off the request when it does not"* — two sentences about one event, one of them surface-blind.
   *
   * The model already draws the line and the prose did not: a default **prevents** the unresolved
   * condition from ever firing (`wire-omission.ts` — *"the value simply exists"*). So this says what
   * the box does, and hands the *what happens when nothing resolves* question to the one control
   * that answers it per surface.
   */
  default:
    'Sent when the value resolves to nothing. With a default set the row always resolves, so the on-the-wire answer never has to decide. Leave it blank and that answer decides instead.',

  /**
   * Zone 4 · **is this on the wire**. Named for its subject, not for one of its conditions (`X46a`).
   *
   * **It described one answer of three** until 2026-09-04 (#1179) — *Never send it* — which is the
   * *offered name with no writer* defect inverted: three answers, one explained. The `?` carries the
   * **vocabulary**, because that is true on every surface; **what each answer does** is the live line
   * under the select, which varies by surface and is `describeOmissionAnswer`'s.
   *
   * The *never* clause stays here as well as there, because it is the one an operator cannot infer
   * from any label: it is the only answer that also removes what the driver built.
   */
  omission:
    'Whether this row goes out at all — always, only when something resolves, or never. The line beneath says what your answer does on this surface. Never send it also removes whatever the driver would otherwise have built at this path.',

  /**
   * Zone 4 · **Type**, the scalar coercion (#923).
   *
   * **It was *Send as* until 2026-09-04 (`#1246`), and the label was false on half its surfaces.**
   * Response mapping stores `MappingEntryDto.asType` and sends nothing — the coercion is what turns
   * `"12.50"` into a number before it is written onto the ad. The control belongs there; the verb did
   * not. `Type` is direction-neutral and correct on both, which is why it is one word rather than a
   * word per surface — that would be `X3` arriving again, one concept under two names.
   *
   * *"before it is sent"* went with the label, for the same reason: a sentence that outlives the
   * word it explained is the harder half of a rename to notice.
   *
   * **A declaration since `4e`** (`FD11` §5, Todd 2026-09-23): the first sentence used to be *coerce
   * the resolved value to this type*, which has no meaning for a list. The help now says what the
   * value **is** and what happens to each kind, and names the first option, because that option is
   * the inherited answer and nothing else on the row says so.
   *
   * The last sentence is `X27`'s explicitly preserved property: *"a value that is only a macro
   * keeps its own type."* It was the Macro tab's help text, which was the only place it was said,
   * and the tab is gone — so it lives here, as a statement about the value rather than about a tab.
   */
  sendAs:
    'What this value is. Text, a number or true/false is converted to that type where it can be; a list or an object is left as it is, and compared with what this field takes where the field declares it. The first option is what applies when you choose nothing here — what the expression declares, or what it produces. A value that is only a macro already keeps its own type.',

  /**
   * **A shared expression's `Type`** (`FD11` §5, `4e`) — what it yields, declared once so every row
   * that uses it reads the same answer. It names the row's override because that is the thing an
   * author of a shared expression cannot see from here.
   */
  expressionYield:
    'What this expression yields. Every row that uses it treats its value as this type unless the row sets its own. auto keeps whatever type the value resolves to, and says what that is where it can tell.',

  /**
   * Zone 4 · **send structured values as**. The scope is in the label (`SendAsControl`'s own
   * docblock records why the prose could not carry it), so this only has to say what the choice is
   * about.
   */
  sendStructuredAs:
    'When a structured value is not supported here, send it as… An object or a nested list needs one of these to go out on a surface that cannot carry structure. A plain list already has a wire form, so it is sent as it is whatever you choose here — as is any single value.',

  /**
   * **The note, which is not a wire capability** (Todd, 2026-09-03) — see `MappingControl`'s
   * `NoteControl` for why it left the chip row.
   */
  note: 'Say what this mapping is for. It travels with the value and shows on the row.',

  /**
   * `E3` (`#1271`). Two sentences because there are two things an operator cannot guess: that some
   * operations take no operands at all, and where the min/max for a random number went.
   */
  operation:
    'How the operands combine — joined as text, or arithmetic. The second group produces a value on its own and takes no operands, so a random number carries its range here.',

  /**
   * `X43`'s reading rule, which the row shows with the symbol between operands and this states in
   * words — including the way out, since *use a group* is unguessable from a control that has none
   * until you make one.
   *
   * **The third sentence is where the default went** (`#1299`, Todd 2026-09-10): an operand carries
   * none, and the calculation carries one for all of them. Said **here**, once per calculation, and
   * not on each operand row — a claim true of the whole group belongs beside the thing that names the
   * group, and repeating it down the rows buries the symbol that varies (`#1248`).
   *
   * It is a **positive statement about where the answer is**, not an apology for a missing control.
   * The distinction matters: a sentence explaining an absence teaches an operator to look for things
   * that are not there.
   */
  operands:
    'Applied left to right, with no precedence: a − b × c is (a − b) × c. To change that, make an operand a calculation of its own with Calculate — it is worked out first, like a bracket. If any operand resolves to nothing the whole calculation falls back to its default, so the default belongs on the calculation rather than on a row. An operand ending in [*] — pod.cachedBidRefs[*].bid — is every member of a list, so add over it is a sum and avg its average.',

  /**
   * `X41`'s reading rule for a conditional, as `operands` is `X43`'s for a calculation. It was a
   * standing sentence above every ladder with more than one case; it never changes, so it is teaching
   * (#1179), and the `?` is offered only where the sentence was — where there is an order to state.
   */
  caseOrder: 'The first case that matches decides the value. Later cases are not consulted.',
} as const;

/** Hover-help for the capability chips (`X25`, `X28b`) — what each does, never why it is unavailable. */
export const CHIP_HELP = {
  if: "Send one value when a condition holds, and another when it doesn't.",
  calculate: 'Combine several values — a floor scaled by a factor, a name joined to an id.',

  shared: 'Address an expression from the library, so one change reaches every place that uses it.',
  tested: 'Use the value an earlier case tested — compute with it, or send it — without authoring it twice.',
  extract: 'Pull part of the resolved value out with a regular expression.',
  transform: 'Change the case of the resolved value, or trim its whitespace.',
} as const;

/**
 * **What a row on a list surface can hold** — one sentence for parameters and headers (#1179).
 *
 * It was written twice, as a footer on the parameter editor and in a hand-rolled `ⓘ` on the headers
 * editor, and the two copies had already drifted: only one said when macros and paths resolve.
 * *Shared logic is not shared words* (#1187) — one decision, one sentence.
 */
export const rowValueResolves = (noun: string): string =>
  `Each ${noun} resolves as a static value, a macro, a context path, or an expression. Macros and paths resolve at request time against the session.`;
